import { useEffect, MutableRefObject } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import compact from 'lodash/compact';
import {
    BLUR_COMMAND,
    FOCUS_COMMAND,
    KEY_ENTER_COMMAND,
    KEY_SPACE_COMMAND,
    COMMAND_PRIORITY_LOW,
    COMMAND_PRIORITY_EDITOR,
} from 'lexical';

interface CommandListenersPluginProps {
    allowEnterNewLine?: boolean;
    onBlur?: () => void;
    onEditable?: (isEditable: boolean) => void;
    onFocus?: () => void;
    onEnterDown?: () => void;
    onSpacePress?: () => void;
    mentionOptionsPortalRef?: MutableRefObject<HTMLDivElement | null>;
}

export const CommandListenersPlugin = ({
    allowEnterNewLine = false,
    onBlur,
    onEditable,
    onEnterDown,
    onSpacePress,
    onFocus,
    mentionOptionsPortalRef,
}: CommandListenersPluginProps): null => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        const removeListeners = compact([
            onBlur &&
                editor.registerCommand(
                    BLUR_COMMAND,
                    () => {
                        onBlur();
                        return false;
                    },
                    COMMAND_PRIORITY_EDITOR
                ),

            onEditable &&
                editor.registerEditableListener((isEditable: boolean) => {
                    onEditable(isEditable);
                }),
            onSpacePress &&
                editor.registerCommand(
                    KEY_SPACE_COMMAND,
                    () => {
                        if (mentionOptionsPortalRef?.current) {
                            onSpacePress();
                            mentionOptionsPortalRef.current = null;
                            return false;
                        }
                        return true;
                    },
                    COMMAND_PRIORITY_LOW
                ),
            onEnterDown &&
                editor.registerCommand(
                    KEY_ENTER_COMMAND,
                    (event) => {
                        if (mentionOptionsPortalRef?.current) {
                            // clear mentionOptionsPortalRef, otherwise next Enter press will fall into this if statement again.
                            mentionOptionsPortalRef.current = null;
                            return false;
                        }
                        if (event && !event.shiftKey) {
                            onEnterDown();
                            if (allowEnterNewLine) {
                                return false;
                            } else {
                                event.preventDefault();
                                return true;
                            }
                        }
                        return false;
                    },
                    COMMAND_PRIORITY_LOW
                ),
            onFocus &&
                editor.registerCommand(
                    FOCUS_COMMAND,
                    () => {
                        onFocus();
                        return false;
                    },
                    COMMAND_PRIORITY_LOW
                ),
        ]);

        return () => {
            removeListeners.forEach((removeListener) => removeListener());
        };
    }, [
        editor,
        onBlur,
        onEditable,
        onEnterDown,
        onFocus,
        onSpacePress,
        mentionOptionsPortalRef,
        allowEnterNewLine,
    ]);

    return null;
};
